import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast, Toaster } from "sonner";
import Navbar from "@/components/Navbar";
import { errMsg } from "@/lib/errors";

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [role, setRole] = useState(sp.get("role") === "provider" ? "provider" : "customer");
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", postcode: "", business_name: "", category: "" });
  const [busy, setBusy] = useState(false);
  const [cats, setCats] = useState([]);

  // Load the same public /api/categories list the customer request form
  // already uses, so provider signup and customer requests always share the
  // exact same Service/Trade taxonomy.
  useEffect(() => {
    api.get("/categories").then(({ data }) => setCats(data || [])).catch(() => setCats([]));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    // Provider signup requires phone, postcode AND trade so the admin has
    // everything they need to review the account before approving.
    if (role === "provider") {
      if (!form.category) { toast.error("Please pick your service / trade"); return; }
      if (!form.postcode?.trim()) { toast.error("Please enter the postcode / area you work in"); return; }
    }
    setBusy(true);
    try {
      const u = await signup({ ...form, role });
      toast.success("Account created!");
      // Google Ads conversion — fire only when a PROVIDER signup completed
      // successfully (i.e. the API returned an account with role=provider).
      // Fires exactly once per successful signup, never on page loads or
      // customer signups.
      if (u?.role === "provider" && typeof window !== "undefined" && typeof window.gtag === "function") {
        try {
          window.gtag("event", "conversion", { send_to: "AW-18417300294/I8HfCNyDuuscEMbmhs5E" });
        } catch (_) { /* never let analytics break signup flow */ }
      }
      nav(u.role === "provider" ? "/provider" : "/customer");
    } catch (err) {
      toast.error(errMsg(err, "Sign up failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <Toaster position="top-center" />
      <div className="max-w-md mx-auto px-4 py-14">
        <h1 className="font-display text-3xl font-bold text-slate-900">Create your FixNear account</h1>

        <div className="mt-6 grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-full">
          <button data-testid="signup-role-customer" onClick={() => setRole("customer")} className={`rounded-full py-2 text-sm font-semibold transition-colors ${role === "customer" ? "bg-white text-slate-900 shadow" : "text-slate-600"}`}>I need help</button>
          <button data-testid="signup-role-provider" onClick={() => setRole("provider")} className={`rounded-full py-2 text-sm font-semibold transition-colors ${role === "provider" ? "bg-white text-slate-900 shadow" : "text-slate-600"}`}>I&apos;m a service pro</button>
        </div>

        {role === "provider" && (
          <div className="mt-4 text-xs text-slate-500 bg-emerald-50 border border-emerald-100 rounded-xl p-3">
            Quick provider signup — no documents required. We just need your name, phone, email, service and working postcode.
          </div>
        )}

        <form onSubmit={submit} className="mt-6 card space-y-4">
          <div>
            <label className="label">Full name</label>
            <input data-testid="signup-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="field" />
          </div>
          {role === "provider" && (
            <div>
              <label className="label">Business name <span className="text-slate-400 font-normal">(optional)</span></label>
              <input data-testid="signup-business" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="field" placeholder="e.g. Macie Plumbing Ltd" />
            </div>
          )}
          <div>
            <label className="label">Email</label>
            <input data-testid="signup-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="field" />
          </div>
          <div>
            <label className="label">Phone{role === "provider" ? "" : ""}</label>
            <input data-testid="signup-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="field" placeholder="+44 …" />
          </div>
          {role === "provider" && (
            <div>
              <label className="label">Service / Trade</label>
              <select
                data-testid="signup-category"
                required
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="field"
              >
                <option value="">Choose your trade…</option>
                {cats.map((c) => (
                  <option key={c.slug} value={c.slug}>{c.name}</option>
                ))}
              </select>
              <div className="text-xs text-slate-500 mt-1">You can add more trades later from your provider dashboard.</div>
            </div>
          )}
          <div>
            <label className="label">
              {role === "provider" ? "Postcode / working area" : "Postcode"}
              {role === "customer" && <span className="text-slate-400 font-normal"> (optional)</span>}
            </label>
            <input
              data-testid="signup-postcode"
              required={role === "provider"}
              value={form.postcode}
              onChange={(e) => setForm({ ...form, postcode: e.target.value })}
              className="field"
              placeholder="L1 8JQ"
            />
          </div>
          <div>
            <label className="label">Password</label>
            <input data-testid="signup-password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="field" />
          </div>
          <button data-testid="signup-submit" disabled={busy} className="btn-accent w-full">{busy ? "Creating…" : "Create account"}</button>
          <div className="text-sm text-slate-600 text-center">Already have an account? <Link to="/login" className="text-emerald-600 font-semibold">Log in</Link></div>
        </form>
      </div>
    </div>
  );
}
